import React, { useState } from 'react';
import { Mail, MapPin, Send, CheckCircle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Link as RouterLink } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SUBJECTS = [
  { value: '', label: 'Sélectionner un sujet...' },
  { value: 'Question patient', label: '🏥 Question patient' },
  { value: 'Question médecin', label: '👨‍⚕️ Question médecin' },
  { value: 'Problème technique', label: '🔧 Problème technique' },
  { value: 'Partenariat', label: '🤝 Partenariat' },
  { value: 'Autre', label: '💬 Autre' },
];

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.subject) {
      toast.error('Veuillez sélectionner un sujet');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/contact`, {
        name: form.name,
        email: form.email,
        message: `[${form.subject}] ${form.message}`,
      });
      setSent(true);
      toast.success('Message envoyé ! Vous recevrez une confirmation par email.');
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast.error("Erreur lors de l'envoi. Réessayez ou contactez-nous par email.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold text-[#0F172A] font-['Outfit']">Contactez-nous</h1>
          <p className="text-[#64748B] mt-2">Une question ? Nous vous répondons dans les plus brefs délais.</p>
        </div>

        {/* Lien vers partenariat */}
        <div className="bg-[#E6F4FB] border border-[#0596DE]/20 rounded-2xl p-4 mb-6 flex items-center justify-between">
          <div>
            <p className="font-medium text-[#0F172A] text-sm">Vous êtes un établissement de santé ?</p>
            <p className="text-xs text-[#64748B] mt-0.5">Clinique, hôpital, pharmacie — découvrez notre offre partenariat</p>
          </div>
          <RouterLink
            to="/partenariat"
            className="text-sm font-semibold text-[#0596DE] hover:underline whitespace-nowrap ml-4"
          >
            Voir l'offre →
          </RouterLink>
        </div>

        <div className="bg-white rounded-2xl border border-[#E2E8F0] p-8 shadow-sm">
          {sent ? (
            <div className="text-center py-12">
              <CheckCircle className="w-16 h-16 text-[#10B981] mx-auto mb-4" />
              <h2 className="text-xl font-bold text-[#0F172A] mb-2">Message envoyé !</h2>
              <p className="text-[#64748B] mb-6">
                Nous avons bien reçu votre message. Une confirmation a été envoyée à votre email.
              </p>
              <button
                onClick={() => setSent(false)}
                className="px-6 py-2 rounded-xl bg-[#0596DE] text-white font-semibold hover:bg-[#047CBA] transition-colors"
              >
                Envoyer un autre message
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label className="mb-1 block">Nom complet</Label>
                <Input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Votre nom"
                  required
                  className="rounded-xl"
                />
              </div>

              <div>
                <Label className="mb-1 block">Adresse email</Label>
                <Input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="votre@email.com"
                  required
                  className="rounded-xl"
                />
              </div>

              <div>
                <Label className="mb-1 block">Sujet</Label>
                <select
                  name="subject"
                  value={form.subject}
                  onChange={handleChange}
                  required
                  className="w-full border border-[#E2E8F0] rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0596DE] bg-white"
                >
                  {SUBJECTS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label className="mb-1 block">Message</Label>
                <Textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  required
                  placeholder="Décrivez votre demande..."
                  className="min-h-[140px] rounded-xl"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl bg-[#0596DE] hover:bg-[#047CBA] h-12 text-base font-semibold text-white transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading ? (
                  <span className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Envoyer le message
                  </>
                )}
              </button>
            </form>
          )}

          <div className="mt-8 pt-6 border-t border-[#E2E8F0] grid md:grid-cols-2 gap-4 text-sm text-[#64748B]">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#0596DE]" />
              <span>contact@tabbibak.com</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#0596DE]" />
              <span>Tizi Ouzou & Oran, Algérie</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
